import { useCallback, useEffect, useMemo } from "react";
import { buildShowIfVisibility, hasStoredFieldValue } from "../utils/showIf.utils";

/**
 * Memoizes Show If visibility from smartChipResponses and clears hidden field values in an effect.
 * @param {Object} params
 * @param {Array} params.sections - Active tab subsections
 * @param {Object} params.smartChipResponses - Expression + chip value source
 * @param {Object} params.formResponses - Element id keyed responses
 * @param {boolean} params.isInteractive - When true, clears values for newly hidden fields
 * @param {Function} [params.onFieldChange] - (fieldId, value, smartChipId) => void
 * @returns {{ isElementVisible: Function, isSubsectionVisible: Function }}
 * @author Abhishek Dhillon
 */
export const useShowIfVisibility = ({
  sections = [],
  smartChipResponses = {},
  formResponses = {},
  isInteractive = false,
  onFieldChange,
}) => {
  const { elementVisibility, subsectionVisibility, hiddenFields } = useMemo(
    () => buildShowIfVisibility(sections, smartChipResponses),
    [sections, smartChipResponses]
  );

  /** Stable key so the cleanup effect runs only when the hidden set changes, not on every formResponses update. */
  const hiddenFieldsKey = useMemo(
    () => hiddenFields.map(({ id }) => id).sort().join(","),
    [hiddenFields]
  );

  const isElementVisible = useCallback((elementId) => elementVisibility[elementId] !== false, [elementVisibility]);

  const isSubsectionVisible = useCallback((subsectionId) => subsectionVisibility[subsectionId] !== false, [subsectionVisibility]);

  useEffect(() => {
    if (!isInteractive || !onFieldChange || !hiddenFieldsKey) return;

    hiddenFields.forEach(({ id, smartChipId }) => {
      if (hasStoredFieldValue(id, smartChipId, formResponses, smartChipResponses)) {
        onFieldChange(id, null, smartChipId);
      }
    });
  }, [hiddenFieldsKey, hiddenFields, formResponses, smartChipResponses, isInteractive, onFieldChange]);

  return { isElementVisible, isSubsectionVisible };
};
